'use client';

import { Tabs as TabsPrimitive } from 'radix-ui';
import { motion } from 'framer-motion';
import * as React from 'react';

import { cn } from '@/lib/cn';

const TabsGroupIdContext = React.createContext('');

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>((props, ref) => {
  const tabsGroupId = React.useId();

  return (
    <TabsGroupIdContext.Provider value={tabsGroupId}>
      <TabsPrimitive.Root ref={ref} {...props} />
    </TabsGroupIdContext.Provider>
  );
});

Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-lg bg-muted-foreground/10 p-1 text-foreground dark:bg-muted-foreground/5',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const tabsGroupId = React.useContext(TabsGroupIdContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'group relative inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm whitespace-nowrap outline-hidden disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {/* We can use the content primitive to get the internal radix state and
      only render the motion div, we need to remove some attributes though */}
      <TabsPrimitive.Content value={props.value} asChild>
        <motion.div
          id={undefined}
          tabIndex={-1}
          layoutId={tabsGroupId}
          aria-labelledby={undefined}
          className='absolute inset-0 z-0 bg-surface shadow-input outline-offset-2 outline-primary group-focus-visible:outline-1'
          style={{
            // must use inline style for framer to not distort
            borderRadius: '6px',
          }}
          transition={{ duration: 0.1 }}
        />
      </TabsPrimitive.Content>
      <div className='relative z-10'>{props.children}</div>
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden data-[state=inactive]:hidden',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
